import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Download, ChevronDown, ChevronRight } from "lucide-react";
import { adminApi } from "@/lib/api";
import { fmtDate, downloadCSV } from "@/lib/format";
import { PageHeader, EmptyState, useDebounced } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const PAGE = 50;

export default function AdminAuditLogs() {
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("all");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);
  const s = useDebounced(search, 350);
  const params = { search: s || undefined, entity_type: entity === "all" ? undefined : entity };
  const { data, isLoading } = useQuery({ queryKey: ["admin-audit", params, page], queryFn: () => adminApi.get("/admin/audit-logs", { params: { ...params, limit: PAGE, skip: page * PAGE } }).then((r) => r.data), refetchInterval: 30000 });
  const exportCsv = async () => {
    const r = await adminApi.get("/admin/audit-logs", { params: { ...params, limit: 1000 } });
    downloadCSV(`audit-log-${Date.now()}.csv`, r.data.items.map((a) => ({ created_at: a.created_at, actor: a.actor_email, action: a.action, entity_type: a.entity_type, entity_id: a.entity_id, before: JSON.stringify(a.before), after: JSON.stringify(a.after), note: a.note || "" })));
  };
  return (
    <div>
      <PageHeader title="Audit log" subtitle="Append-only record of every administrative action." actions={<Button variant="outline" className="gap-2 bg-white" onClick={exportCsv} data-testid="audit-export-csv"><Download className="h-4 w-4" /> Export CSV</Button>} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search action, admin, entity ID" className="h-10 bg-white pl-9" data-testid="audit-search-input" />
        </div>
        <Select value={entity} onValueChange={(v) => { setEntity(v); setPage(0); }}>
          <SelectTrigger className="h-10 bg-white sm:w-52" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {["payment_method", "deposit_request", "bonus_config", "exchange_rates", "user"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {isLoading ? <Skeleton className="h-96" /> : data.items.length === 0 ? <EmptyState title="No audit entries" /> : (
        <Card className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow><TableHead className="w-8" /><TableHead>When</TableHead><TableHead>Admin</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.map((a) => (
                <React.Fragment key={a.id}>
                  <TableRow className="cursor-pointer hover:bg-muted/60" onClick={() => setOpen(open === a.id ? null : a.id)} data-testid="audit-row">
                    <TableCell>{open === a.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{fmtDate(a.created_at)}</TableCell>
                    <TableCell className="text-sm">{a.actor_email}</TableCell>
                    <TableCell><span className="rounded bg-secondary px-2 py-0.5 font-mono text-xs">{a.action}</span></TableCell>
                    <TableCell className="text-xs"><span className="text-muted-foreground">{a.entity_type}</span><span className="num block max-w-[200px] truncate">{a.entity_id}</span></TableCell>
                    <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground">{a.note}</TableCell>
                  </TableRow>
                  {open === a.id && (
                    <TableRow><TableCell colSpan={6} className="bg-slate-50">
                      <div className="grid gap-3 md:grid-cols-2">
                        <div><p className="mb-1 text-xs font-semibold">Before</p><pre className="max-h-64 overflow-auto rounded-lg border bg-white p-3 text-[11px]">{JSON.stringify(a.before, null, 2)}</pre></div>
                        <div><p className="mb-1 text-xs font-semibold">After</p><pre className="max-h-64 overflow-auto rounded-lg border bg-white p-3 text-[11px]">{JSON.stringify(a.after, null, 2)}</pre></div>
                      </div>
                    </TableCell></TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {data?.total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{page * PAGE + 1}–{Math.min(data.total, (page + 1) * PAGE)} of {data.total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="audit-prev">Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * PAGE >= data.total} onClick={() => setPage(page + 1)} data-testid="audit-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
